import { ServiceStoreConfig } from "../types/ServiceStoreTypes";

/**
 * AI Agent (Rosie, the office manager) ServiceStores — the five `ai-agent-*` paths the chat surface
 * actually reads.
 *
 * `@intrasync/ai-agent-api` registers NINETEEN services (agents, prompts, tools,
 * knowledge-bases, documents, document-chunks, retrieval, conversations,
 * messages, message-feedback, runs, tool-invocations, trace-spans,
 * ingestion-jobs, proposed-actions, scheduled-runs, agent-triggers,
 * usage-report, usage-budgets).
 *
 * FIVE WERE MOUNTED UNTIL 2026-08-26, deliberately: this module was a chat
 * client for the seeded agent (catalog row `isa-hq`), not an admin console. That call was
 * right at the time and has been overtaken by one — the operator asked for the
 * platform's own surfaces, so the Agent Admin view (views/aiAgent/AgentAdmin)
 * now needs usage, prompts, tools, knowledge and automation.
 *
 * The condition the old note set is the one being met: "unmounted until
 * something in the UI needs them". Each block below says which tab needs it, so
 * a store with no reader is visible as such rather than accumulating quietly.
 *
 * Notes that matter when reading these:
 * - Every path is PREFIXED `ai-agent-*` (RUNS_PATH = 'ai-agent-runs', …). There
 *   is no bare `runs`/`documents` — see packages/api/src/services/ai-agent-host.ts.
 * - `companyId` is stamped and query-scoped by the API host's tenancy hook on
 *   every EXTERNAL call, so the client never sends (and must never trust) it.
 * - Ids are server-minted UNIQUEIDENTIFIERs, always `id`.
 * - `serviceView.enableServiceView` is deliberately NOT set: these must not
 *   sprout auto-generated CRUD routes under Management. The agent platform is
 *   append-only and orchestration-driven; a generic grid over `ai-agent-runs`
 *   would offer edit/remove affordances the API does not implement.
 */
export const aiAgentStores: ServiceStoreConfig[] = [
  // Agent catalog. Read-only here — the chat resolves the seeded `isa-hq` row (Rosie) to
  // get its id, model and tool allowlist. Seeded by the API on every cold start
  // (services/ai-agent/office-manager.seed.ts), never created from the web.
  { name: "ai-agent-agents", options: { idField: "id", enableStorage: false } },
  // Conversation threads. find/get/create/patch (patch is title+status only —
  // "archive" is `status: 'archived'`; conversations are never deleted).
  {
    name: "ai-agent-conversations",
    options: { idField: "id", enableStorage: false },
  },
  // Append-only conversation turns (role user/assistant/tool). find/get/create.
  // `content` is a ContentBlock[] and arrives DECODED from the API's external
  // resolver, so no JSON.parse is needed on the client.
  //
  // Realtime: HQ's channels.ts scopes every ai-agent row to its conversation's
  // owner (`user/<id>`), so this cache only ever receives the signed-in user's
  // own turns. (It used to broadcast to the whole `internal` channel.) The
  // view still filters by `conversationId`, because several of the user's own
  // threads can be cached at once — never render `entities` unfiltered.
  {
    name: "ai-agent-messages",
    options: { idField: "id", enableStorage: false },
  },
  // The orchestration record for one assistant turn. Client-immutable:
  // find/get/create plus the custom `start`/`cancel` action methods, which the
  // Feathers socket client does not expose by default — see
  // views/aiAgent/AgentChat/runActions.ts for that seam.
  { name: "ai-agent-runs", options: { idField: "id", enableStorage: false } },
  // Per-tool-call audit rows (toolName, input, output, status, durationMs).
  // This is what lets a read-only agent show WHERE its answer came from.
  {
    name: "ai-agent-tool-invocations",
    options: { idField: "id", enableStorage: false },
  },

  // ── Agent Admin ───────────────────────────────────────────────────────────
  //
  // Read-mostly. `serviceView.enableServiceView` stays OFF for every one of
  // these, for the reason given above: the agent platform is append-only and
  // orchestration-driven, so a generic CRUD grid would offer edit and remove
  // affordances the API does not implement.

  // Run trace tab — the per-span timing behind an answer. tool-invocations
  // (already mounted) says WHICH tools ran; spans say what the run spent its
  // time on, including the model call itself.
  { name: "ai-agent-trace-spans", options: { idField: "id", enableStorage: false } },

  // Usage tab. `ai-agent-usage-report` exposes ONE custom method, `report`
  // (AgentAdminStore calls it through getAgentMethods); the API registers no
  // `find`. enableOffline OFF is therefore REQUIRED, exactly as for
  // instance-actions: the offline wrapper's blanket sync-on-reconnect is a
  // bare find(), which answered "Method 'find' not allowed on service
  // 'ai-agent-usage-report'" on every socket (re)connect and route enter.
  {
    name: "ai-agent-usage-report",
    options: { idField: "id", enableStorage: false, enableOffline: false },
  },
  { name: "ai-agent-usage-budgets", options: { idField: "id", enableStorage: false } },

  // Prompts & Tools tab. `ai-agent-tools` is the catalog the API seeds from
  // hq-tool-catalog.ts — editable here only in the fields the API allows.
  { name: "ai-agent-prompts", options: { idField: "id", enableStorage: false } },
  { name: "ai-agent-tools", options: { idField: "id", enableStorage: false } },
  { name: "ai-agent-message-feedback", options: { idField: "id", enableStorage: false } },

  // Knowledge tab (RAG).
  { name: "ai-agent-knowledge-bases", options: { idField: "id", enableStorage: false } },
  { name: "ai-agent-documents", options: { idField: "id", enableStorage: false } },
  { name: "ai-agent-ingestion-jobs", options: { idField: "id", enableStorage: false } },

  // Automation tab.
  { name: "ai-agent-scheduled-runs", options: { idField: "id", enableStorage: false } },
  { name: "ai-agent-agent-triggers", options: { idField: "id", enableStorage: false } },
  { name: "ai-agent-proposed-actions", options: { idField: "id", enableStorage: false } }

];
